import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Brain, Mail, Lock, User, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api'; // WICHTIG: Deine API-Instanz nutzen
import { toast } from 'sonner';

export default function RegisterPage() {
  const navigate = useNavigate();
  
  // States für Formular
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // UI States
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 1. Validierung (aus der UHR-Version übernommen)
    if (!username || !email || !password || !confirmPassword) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen haben');
      return;
    }

    setLoading(true);

    try {
      // 2. API Aufruf (Deine Logik aus der Server-Version)
      const response = await api.post('/auth/register', { 
        username, 
        email, 
        password 
      });

      // ✅ Fall A: Admin muss freischalten (Status 202)
      if (response.status === 202) {
        toast.success('Konto erstellt! Bitte warte auf die Freigabe durch den Admin.', { duration: 6000 });
        navigate('/login'); 
        return;
      }

      // ✅ Fall B: Sofort-Login (falls Token zurückkommt)
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        if (response.data.user) {
            localStorage.setItem('user', JSON.stringify(response.data.user));
        }
        
        toast.success('Willkommen bei FreeBrain!');
        // Hard-Reload um sicherzustellen, dass Auth-Status greift
        window.location.href = '/'; 
      }

    } catch (error) {
      const message = error.response?.data?.detail || 'Registrierung fehlgeschlagen';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4 overflow-hidden">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl p-8 space-y-8 border border-slate-100 dark:border-slate-800">
          
          {/* Header / Logo */}
          <div className="text-center space-y-2">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-500 text-white mb-4 shadow-lg shadow-emerald-500/20"
            >
              <Brain className="w-8 h-8" />
            </motion.div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
              Konto erstellen
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              Werde Teil von FreeBrain
            </p>
          </div>

          {/* Formular */}
          <form onSubmit={handleSubmit} className="space-y-5">
            
            {/* Benutzername */}
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-700 dark:text-slate-300">
                Benutzername
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="username"
                  type="text"
                  placeholder="Dein Name"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-10 h-12 rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:border-emerald-500 transition-colors"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 dark:text-slate-300">
                E-Mail Adresse
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="name@beispiel.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12 rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:border-emerald-500 transition-colors"
                  required
                />
              </div>
            </div>

            {/* Passwort */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 dark:text-slate-300">
                Passwort
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-12 rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:border-emerald-500 transition-colors"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Passwort Bestätigen */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-700 dark:text-slate-300">
                Passwort bestätigen
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 h-12 rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:border-emerald-500 transition-colors"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-lg shadow-emerald-500/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                'Registrieren'
              )}
            </Button>
          </form>

          {/* Footer Link */}
          <p className="text-center text-slate-500 dark:text-slate-400 text-sm">
            Bereits ein Konto?{' '}
            <Link
              to="/login"
              className="text-emerald-500 hover:text-emerald-600 font-medium hover:underline transition-all"
            >
              Anmelden
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}